export const partnerKeys = {
    all: ['partners'],
    getById: (partnerId: number) => [...partnerKeys.all, partnerId],
    getBySlug: (slug?: string) => [...partnerKeys.all, slug],
    getProjectsById: (partnerId?: number) => [...partnerKeys.all, partnerId, 'projects'],
    getJoinStatusById: (partnerId?: number) => [...partnerKeys.all, partnerId, 'join-status'],
    getLastActiveProject: (partnerId?: number) => [...partnerKeys.all, partnerId, 'last-active-project'],
    postJoin: ['partners', 'join'],
};

export const questKeys = {
    getWithUserStatusById: (partnerId?: number) => ['quests', partnerId, 'user-status'],
};

export const pointsKeys = {
    userPoints: (partnerId?: number) => ['points', partnerId, 'user'],
    pointsEarnedHistory: (partnerId?: number) => ['points', partnerId, 'earned'],
    pointsSpentHistory: (partnerId?: number) => ['points', partnerId, 'spent'],
    pointsMonthlyStats: (partnerId?: number) => ['points', partnerId, 'stats'],
};

export const rarityKeys = {
    getByPartnerId: (partnerId?: number) => ['rarities', partnerId],
};

export const traitsKeys = {
    traitsByPartnerId: (partnerId?: number) => ['traits', partnerId],
    traitsCategoriesByPartnerId: (partnerId?: number) => ['traits', partnerId, 'categories'],
    traitsStackingConfig: (partnerId?: number) => ['traits', partnerId, 'stacking'],
    mintTrait: ['traits', 'mint'],
    confirmMintTrait: ['traits', 'confirm-mint'],
    cancelMintTrait: ['traits', 'cancel-mint'],
    addTraitSignature: ['traits', 'add-signature'],
    pollMintStatus: (queueName: string, jobId: string) => ['traits', 'poll-mint-status', queueName, jobId],
    traitsOwnedByUser: (partnerId?: number) => ['traits', partnerId, 'user-owned'],
};

export const eggKeys = {
    eggByProject: (projectId?: number) => ['egg', projectId],
    checkMintStatus: (eggId?: number, walletAddress?: string) => ['egg', eggId, 'mint-status', walletAddress],
    mint: ['egg', 'mint'],
    mintBuild: ['egg', 'mint-build'],
    confirmMint: ['egg', 'confirm-mint'],
    cancelMint: ['egg', 'cancel-mint'],
    addSignature: ['egg', 'add-signature'],
    pollMintStatus: (queueName?: string, jobId?: string) => ['egg', 'poll-mint-status', queueName, jobId],
};

export const nftKeys = {
    getByProjectId: (projectId?: number) => ['nfts', projectId],
    userCollection: ['nfts', 'user-collection'],
    onchainStats: (partnerId?: number) => ['nfts', partnerId, 'onchain-stats'],
    upgradeNFT: ['nfts', 'upgrade-nft'],
    upgradeNFTBuild: ['nfts', 'upgrade-nft-build'],
    confirmUpgradeNFT: ['nfts', 'confirm-upgrade-nft'],
    cancelUpgradeNFT: ['nfts', 'cancel-upgrade-nft'],
    pollUpgradeStatus: (queueName?: string, jobId?: string) => ['nfts', 'poll-upgrade-status', queueName, jobId],
    mintDirect: ['nfts', 'mint-direct'],
    mintDirectBuild: ['nfts', 'mint-direct-build'],
    confirmDirectMint: ['nfts', 'confirm-direct-mint'],
    cancelDirectMint: ['nfts', 'cancel-direct-mint'],
    addDirectSignature: ['nfts', 'add-direct-signature'],
    checkSwapRequirement: ['nfts', 'check-swap-requirement'],
    claimNft: ['nfts', 'claim-nft'],
    claimNftBuild: ['nfts', 'claim-nft-build'],
    confirmClaimNft: ['nfts', 'confirm-claim-nft'],
    pollClaimStatus: (queueName?: string, jobId?: string) => ['nfts', 'poll-claim-status', queueName, jobId],
    syncCollection: ['nfts', 'sync-collection'],
    pollSyncStatus: (queueName?: string, jobId?: string) => ['nfts', 'poll-sync-status', queueName, jobId],
};

export const walletKeys = {
    postConnectWallet: ['wallet', 'connect'],
    getWalletBalance: (publicKey?: string) => ['wallet', publicKey, 'balance'],
    deleteWallet: ['wallet', 'delete'],
    getAllWalletBalances: ['wallet', 'balances'],
    getWalletList: ['wallet', 'list'],
    getPrimaryWallet: ['wallet', 'primary'],
    tokenStats: (tokenName?: string) => ['wallet', 'token-stats', tokenName],
};

export const faucetKeys = {
  all: ["faucet"] as const,
  claim: ["faucet", "claim"],
  config: (partnerId?: number) => ["faucet", partnerId, "config"] as const,
  status: (partnerId?: number, walletAddress?: string) => ["faucet", partnerId, "status", walletAddress] as const,
  stats: (partnerId?: number) => ["faucet", partnerId, "stats"] as const,
  airdropLockToken: ["faucet", "airdrop-lock-token"] as const,
};

export const codeVerificationKeys = {
  status: (projectId?: number) => ['code-verification', projectId, 'status'] as const,
  verify: ['code-verification', 'verify'] as const,
};

export const jupiterKeys = {
  getOrder: ['jupiter', 'order'] as const,
  executeOrder: ['jupiter', 'execute'] as const,
};

export const authKeys = {
  all: ["auth"] as const,
  me: ["auth", "me"] as const,
  adminStatus: ["auth", "admin-status"] as const,
  login: (provider: string, force?: boolean) => ["auth", "login", provider, force] as const,
  link: (provider: string) => ["auth", "link", provider] as const,
  unlink: ["auth", "unlink"] as const,
  linkedAccounts: ["auth", "linked-accounts"] as const,
  logout: ["auth", "logout"] as const,
};

export const ouraKeys = {
  all: ["oura"] as const,
  protocol: ["oura", "protocol"] as const,
  projects: (params?: unknown) => ["oura", "projects", params] as const,
  projectDetail: (projectId?: number | null) =>
    ["oura", "project-detail", projectId] as const,
  projectCollectionDetail: (projectId?: number | null, collection?: string | null) =>
    ["oura", "project-collection-detail", projectId, collection] as const,
  project: (collection: string, tokenMint: string) =>
    ["oura", "project", collection, tokenMint] as const,
  traits: (params?: unknown) => ["oura", "traits", params] as const,
  traitDetail: (collection?: string | null) => ["oura", "trait-detail", collection] as const,
  trait: (collection: string) => ["oura", "trait", collection] as const,
  operations: (params?: unknown) => ["oura", "operations", params] as const,
  nftVault: (asset: string, collection: string, tokenMint: string) =>
    ["oura", "nft-vault", asset, collection, tokenMint] as const,
};

export const partnerAdminKeys = {
  all: ["partner-admin"] as const,
  partners: (params?: unknown) => ["partner-admin", "partners", params] as const,
  partner: (id?: number | null) => ["partner-admin", "partner", id] as const,
  projects: (partnerId?: number | null) => ["partner-admin", partnerId, "projects"] as const,
  quests: (partnerId?: number | null) => ["partner-admin", partnerId, "quests"] as const,
  eggs: (partnerId?: number | null) => ["partner-admin", partnerId, "eggs"] as const,
  eggPool: (eggId?: number | null) => ["partner-admin", "egg-pool", eggId] as const,
  items: (partnerId?: number | null, filters?: unknown) => ["partner-admin", partnerId, "items", filters] as const,
  rarities: (partnerId?: number | null) => ["partner-admin", partnerId, "rarities"] as const,
  categories: (partnerId?: number | null) => ["partner-admin", partnerId, "categories"] as const,
  tokenList: ["partner-admin", "token-list"] as const,
  featuringPartners: ["partner-admin", "featuring-partners"] as const,
};

export const rbacKeys = {
  all: ["rbac"] as const,
  users: (params?: unknown) => ["rbac", "users", params] as const,
  userRoles: (userId?: number | null) => ["rbac", "users", userId, "roles"] as const,
  userPartners: (userId?: number | null) => ["rbac", "users", userId, "managed-partners"] as const,
  roles: ["rbac", "roles"] as const,
  roleUsers: (roleName?: string | null) => ["rbac", "roles", roleName, "users"] as const,
  rolePermissions: (roleName?: string | null) => ["rbac", "roles", roleName, "permissions"] as const,
  permissions: ["rbac", "permissions"] as const,
  partnerAdmins: (partnerId?: number | null) => ["rbac", "partners", partnerId, "admins"] as const,
  whitelist: ["rbac", "whitelist"] as const,
};
